import type { ReactElement } from "react";
import { AlertTriangle } from "lucide-react";

interface ValidationWarningIndicatorProps {
  warnings: string[];
}

/**
 * Minimalist validation warning indicator
 * Displays as a small yellow triangle in bottom-left corner
 * Shows tooltip with warnings on hover
 */
export function ValidationWarningIndicator({
  warnings,
}: Readonly<ValidationWarningIndicatorProps>): ReactElement | null {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="absolute bottom-2 left-2 z-10 group">
      {/* Warning icon */}
      <div className="bg-yellow-900/90 backdrop-blur-sm border border-yellow-700/50 rounded p-1.5 shadow-lg">
        <AlertTriangle className="h-3.5 w-3.5 text-yellow-400" />
      </div>

      {/* Tooltip - appears on hover */}
      <div className="absolute left-0 bottom-full mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
        <div className="bg-neutral-900 border border-yellow-700/30 rounded-lg shadow-xl p-3 min-w-[280px] max-w-[400px]">
          <p className="text-xs font-semibold text-yellow-400 mb-1.5">Quality Warnings</p>
          <ul className="space-y-1">
            {warnings.map((warning) => (
              <li key={warning} className="text-xs text-yellow-200/90 leading-relaxed">
                • {warning}
              </li>
            ))}
          </ul>
        </div>
        {/* Arrow pointer */}
        <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-yellow-700/30" />
      </div>
    </div>
  );
}
